import React, { useState, useEffect } from 'react';
import {
  Mail,
  RefreshCw,
  Send,
  Paperclip,
  CheckCircle2,
  AlertCircle,
  Inbox,
  Clock,
  ArrowRight,
  Search,
  Filter,
  Eye,
  Building2,
  Ticket,
  ChevronRight,
  ExternalLink,
  ShieldCheck,
  LogOut,
  KeyRound,
  AlertTriangle,
  Plus,
  Link2,
} from 'lucide-react';
import { GmailMessageItem, TicketItem, TemporaryCaseItem, GmailDraftItem } from '../types/index.ts';
import { useAuth } from '../context/AuthContext.tsx';
import { getActiveOAuthClientId } from '../lib/gisAuth.ts';

interface GmailCenterPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const GmailCenterPage: React.FC<GmailCenterPageProps> = ({ onNavigate }) => {
  const {
    currentUser,
    currentRole,
    connectGoogleGmail,
    disconnectGoogleGmail,
    getGmailAccessToken,
    gmailAccessToken,
  } = useAuth();

  const [messages, setMessages] = useState<GmailMessageItem[]>([]);
  const [drafts, setDrafts] = useState<GmailDraftItem[]>([]);
  const [tickets, setTickets] = useState<TicketItem[]>([]);
  const [temporaryCases, setTemporaryCases] = useState<TemporaryCaseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState<GmailMessageItem | null>(null);
  const [filterType, setFilterType] = useState<'ALL' | 'PARSED' | 'TICKET_MATCHED' | 'TEMP_CASE_CREATED' | 'REQUIRES_MANUAL_REVIEW'>('ALL');
  const [search, setSearch] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const activeClientId = getActiveOAuthClientId();

  // Live verified profile state from Gmail API
  const [verifiedGmailUser, setVerifiedGmailUser] = useState<{
    emailAddress: string;
    messagesTotal?: number;
    threadsTotal?: number;
  } | null>(null);

  // Email Composer state
  const [showComposer, setShowComposer] = useState(false);
  const [composeForm, setComposeForm] = useState({
    to: 'here4u.helpdesk@ubl.com.pk',
    subject: 'RE: Site Inspection Report & Quotation Submission - Branch ',
    body: 'Dear UBL Facilities Team,\n\nOur field engineer has conducted the on-site survey and prepared the detailed BOQ & Quotation for the reported maintenance work.\n\nPlease find the quotation in the system for your formal approval.\n\nBest Regards,\nNaeem Akhtar\nNaeem Builder & Construction ERP',
    ticketId: '',
  });
  const [isSending, setIsSending] = useState(false);

  // Manual Review State (Requirement 7)
  const [showManualReviewModal, setShowManualReviewModal] = useState<'LINK_TICKET' | 'CREATE_TEMP_CASE' | 'LINK_TEMP_CASE' | null>(null);
  const [selectedReviewTicketId, setSelectedReviewTicketId] = useState('');
  const [selectedReviewTempCaseId, setSelectedReviewTempCaseId] = useState('');
  const [manualTicketNumberInput, setManualTicketNumberInput] = useState('');
  const [manualTempCaseNotes, setManualTempCaseNotes] = useState('');
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);

  // Verify access token directly against Google's Gmail API profile endpoint
  const verifyTokenWithGmail = async (token: string) => {
    try {
      const resp = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/profile', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const profile = await resp.json();
        if (profile?.emailAddress?.toLowerCase() === 'naeembuilder48@gmail.com') {
          setVerifiedGmailUser(profile);
          return profile;
        } else {
          disconnectGoogleGmail();
          setVerifiedGmailUser(null);
          setErrorMessage(
            `Connected Google account (${profile?.emailAddress || 'unknown'}) does not match authorized email naeembuilder48@gmail.com. Please select naeembuilder48@gmail.com.`
          );
          return null;
        }
      } else {
        const errJson = await resp.json().catch(() => null);
        console.warn('Gmail API probe returned non-200:', errJson);
        disconnectGoogleGmail();
        setVerifiedGmailUser(null);
        setErrorMessage(
          errJson?.error?.message
            ? `Gmail API Profile Error: ${errJson.error.message}`
            : 'Gmail API token verification failed. Please reconnect.'
        );
        return null;
      }
    } catch (e: any) {
      console.warn('Network error probing Gmail API:', e.message);
      disconnectGoogleGmail();
      setVerifiedGmailUser(null);
      setErrorMessage(`Network error verifying Gmail profile: ${e.message}`);
      return null;
    }
  };

  useEffect(() => {
    if (gmailAccessToken) {
      verifyTokenWithGmail(gmailAccessToken);
    } else {
      setVerifiedGmailUser(null);
    }
  }, [gmailAccessToken]);

  const fetchGmailData = async () => {
    try {
      setLoading(true);
      const [msgRes, dftRes, tktRes, tcRes] = await Promise.all([
        fetch('/api/gmail/messages', {
          headers: { 'x-user-role': currentRole },
        }).then((r) => (r.ok ? r.json() : [])),
        fetch('/api/gmail/drafts', {
          headers: { 'x-user-role': currentRole },
        }).then((r) => (r.ok ? r.json() : [])),
        fetch('/api/tickets').then((r) => (r.ok ? r.json() : [])),
        fetch('/api/temporary-cases').then((r) => (r.ok ? r.json() : [])),
      ]);

      if (Array.isArray(msgRes)) {
        setMessages(msgRes);
        if (msgRes.length > 0 && !selectedMessage) {
          setSelectedMessage(msgRes[0]);
        }
      }
      if (Array.isArray(dftRes)) setDrafts(dftRes);
      if (Array.isArray(tktRes)) setTickets(tktRes);
      if (Array.isArray(tcRes)) setTemporaryCases(tcRes);
    } catch (err: any) {
      console.warn('Gmail Center load notice:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGmailData();
  }, []);

  // Handle Google OAuth authorization
  const handleConnectGoogle = async () => {
    try {
      setIsConnecting(true);
      setErrorMessage(null);
      const result = await connectGoogleGmail();
      if (result?.token) {
        const profile = await verifyTokenWithGmail(result.token);
        if (profile?.emailAddress && profile.emailAddress.toLowerCase() === 'naeembuilder48@gmail.com') {
          setToastMessage(`Successfully connected and verified Google Gmail: ${profile.emailAddress} (Read-Only)`);
          await handleSyncNowWithToken(result.token);
        }
      }
    } catch (err: any) {
      console.error('OAuth connection error:', err);
      const msg = err.message || '';
      if (msg.includes('popup was blocked') || msg.includes('popup_blocked')) {
        setErrorMessage('Google sign-in popup was blocked by your browser. Please allow popups for this site and click Connect Google Gmail again.');
      } else if (msg.includes('popup was closed') || msg.includes('popup_closed') || msg.includes('popup-closed')) {
        setErrorMessage('Google sign-in window was closed before authorization was completed. Click Connect Google Gmail to retry.');
      } else if (msg.includes('access was denied') || msg.includes('access_denied')) {
        setErrorMessage('Authorization was not granted. Please approve read-only permissions for naeembuilder48@gmail.com.');
      } else {
        setErrorMessage(msg || 'Failed to authenticate with Google Identity Services. Ensure popups are allowed.');
      }
    } finally {
      setIsConnecting(false);
      setTimeout(() => setToastMessage(null), 6000);
    }
  };

  const handleDisconnect = () => {
    disconnectGoogleGmail();
    setVerifiedGmailUser(null);
    setToastMessage('Disconnected from Google Gmail session. Returned to ERP local mode.');
    setTimeout(() => setToastMessage(null), 4000);
  };

  const handleSyncNowWithToken = async (overrideToken?: string) => {
    try {
      setIsSyncing(true);
      setErrorMessage(null);
      const token = overrideToken || getGmailAccessToken();

      // If token is present, we fetch real messages from Gmail REST API
      let ingestedCount = 0;
      if (token) {
        try {
          const listRes = await fetch(
            'https://gmail.googleapis.com/gmail/v1/users/me/messages?maxResults=10&q=to:naeembuilder48@gmail.com',
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (listRes.ok) {
            const listData = await listRes.json();
            const messageItems = listData.messages || [];

            for (const item of messageItems) {
              const msgDetailRes = await fetch(
                `https://gmail.googleapis.com/gmail/v1/users/me/messages/${item.id}?format=full`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              if (msgDetailRes.ok) {
                const detail = await msgDetailRes.json();
                const headers = detail.payload?.headers || [];
                const subject = headers.find((h: any) => h.name.toLowerCase() === 'subject')?.value || 'No Subject';
                const from = headers.find((h: any) => h.name.toLowerCase() === 'from')?.value || 'unknown@sender.com';
                const to = headers.find((h: any) => h.name.toLowerCase() === 'to')?.value || 'naeembuilder48@gmail.com';
                const snippet = detail.snippet || '';

                // Extract body text safely with base64 decoding
                const decodeBase64Text = (b64: string): string => {
                  try {
                    const clean = b64.replace(/-/g, '+').replace(/_/g, '/');
                    const binStr = atob(clean);
                    const bytes = Uint8Array.from(binStr, (c) => c.charCodeAt(0));
                    return new TextDecoder().decode(bytes);
                  } catch {
                    try {
                      return atob(b64.replace(/-/g, '+').replace(/_/g, '/'));
                    } catch {
                      return '';
                    }
                  }
                };

                const extractBody = (payload: any): string => {
                  if (!payload) return '';
                  if (payload.body?.data) {
                    const txt = decodeBase64Text(payload.body.data);
                    if (txt) return txt;
                  }
                  if (payload.parts && Array.isArray(payload.parts)) {
                    for (const part of payload.parts) {
                      if (part.mimeType === 'text/plain' && part.body?.data) {
                        const txt = decodeBase64Text(part.body.data);
                        if (txt) return txt;
                      }
                    }
                    for (const part of payload.parts) {
                      const nested = extractBody(part);
                      if (nested) return nested;
                    }
                  }
                  return '';
                };

                const decodedText = extractBody(detail.payload);
                const bodyText = decodedText || detail.snippet || '';

                // Extract attachments
                const attachmentList: string[] = [];
                const findAttachments = (parts: any[]) => {
                  if (!parts) return;
                  for (const p of parts) {
                    if (p.filename && p.body?.attachmentId) {
                      attachmentList.push(p.filename);
                    }
                    if (p.parts) findAttachments(p.parts);
                  }
                };
                findAttachments(detail.payload?.parts);

                const livePayload = {
                  simulatedEmail: {
                    messageId: detail.id,
                    threadId: detail.threadId,
                    fromEmail: from,
                    fromName: from.split('<')[0].replace(/"/g, '').trim(),
                    toEmail: to,
                    subject: subject,
                    body: bodyText,
                    attachments: attachmentList,
                  },
                  isSimulated: false,
                };

                await fetch('/api/gmail/sync', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'x-user-role': currentRole,
                  },
                  body: JSON.stringify(livePayload),
                });
                ingestedCount++;
              }
            }
          }
        } catch (fetchErr: any) {
          console.warn('Direct Gmail fetch notice:', fetchErr.message);
        }
      }

      if (ingestedCount > 0) {
        setToastMessage(`Live Gmail Sync Successful: Synchronized ${ingestedCount} live messages from naeembuilder48@gmail.com`);
        await fetchGmailData();
      } else {
        // Fallback local sync
        const res = await fetch('/api/gmail/sync', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-user-role': currentRole,
          },
          body: JSON.stringify({ isSimulated: true }),
        });
        const data = await res.json();
        if (res.ok) {
          setToastMessage(`Mailbox Sync Successful: ${data.notice || data.status || 'Messages synchronized'}`);
          await fetchGmailData();
        } else {
          const friendlyError = data.error?.includes('Failed query')
            ? 'Database query encountered an issue. Local cached records were safely retained.'
            : data.error || 'Failed to sync mailbox';
          setErrorMessage(friendlyError);
          // Preserve message list visibility
          await fetchGmailData();
        }
      }
    } catch (err: any) {
      setErrorMessage(err.message?.includes('Failed query') ? 'Database sync notice: local records retained.' : err.message || 'Error syncing mailbox');
      await fetchGmailData();
    } finally {
      setIsSyncing(false);
      setTimeout(() => setToastMessage(null), 6000);
    }
  };

  const handleManualReviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMessage) return;
    try {
      setIsSubmittingReview(true);
      setErrorMessage(null);

      const payload: any = {
        action: showManualReviewModal,
      };

      if (showManualReviewModal === 'LINK_TICKET') {
        if (selectedReviewTicketId) {
          payload.ticketId = Number(selectedReviewTicketId);
        } else if (manualTicketNumberInput.trim()) {
          payload.ticketNumber = manualTicketNumberInput.trim();
        } else {
          setErrorMessage('Please select an existing ticket or enter a ticket number to link.');
          setIsSubmittingReview(false);
          return;
        }
      } else if (showManualReviewModal === 'CREATE_TEMP_CASE') {
        payload.notes = manualTempCaseNotes.trim() || undefined;
        payload.branchCode = selectedMessage.extractedBranchCode;
      } else if (showManualReviewModal === 'LINK_TEMP_CASE') {
        if (!selectedReviewTempCaseId) {
          setErrorMessage('Please select an open temporary case to link.');
          setIsSubmittingReview(false);
          return;
        }
        payload.tempCaseId = Number(selectedReviewTempCaseId);
      }

      const res = await fetch(`/api/gmail/messages/${selectedMessage.id}/manual-review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-role': currentRole,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok) {
        setToastMessage(data.message || 'Manual review updated successfully.');
        setShowManualReviewModal(null);
        setSelectedReviewTicketId('');
        setSelectedReviewTempCaseId('');
        setManualTicketNumberInput('');
        setManualTempCaseNotes('');
        await fetchGmailData();
        if (data.updatedMessage) {
          setSelectedMessage(data.updatedMessage);
        }
      } else {
        setErrorMessage(data.error || 'Failed to apply manual review action.');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Error processing manual review action.');
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSending(true);
      setErrorMessage(null);
      // Create draft first (Safety Guarantee: Saving draft NEVER transmits email)
      const draftRes = await fetch('/api/gmail/drafts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-role': currentRole },
        body: JSON.stringify({
          toEmail: composeForm.to,
          subject: composeForm.subject,
          body: composeForm.body,
          ticketId: composeForm.ticketId ? Number(composeForm.ticketId) : undefined,
        }),
      });
      const draftData = await draftRes.json();
      if (!draftRes.ok) throw new Error(draftData.error || 'Failed to create draft');

      const draftId = draftData.draft.id;

      // Approve draft
      const approveRes = await fetch(`/api/gmail/drafts/${draftId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-role': currentRole },
      });
      const approveData = await approveRes.json();
      if (!approveRes.ok) throw new Error(approveData.error || 'Failed to approve draft');

      // Explicit Send
      const sendRes = await fetch(`/api/gmail/drafts/${draftId}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-role': currentRole },
        body: JSON.stringify({ accessToken: getGmailAccessToken() }),
      });
      const sendData = await sendRes.json();
      if (sendRes.ok) {
        setToastMessage(`Email officially sent and recorded (${sendData.providerMessageId || 'dispatched'})`);
        setShowComposer(false);
        await fetchGmailData();
      } else {
        setErrorMessage(sendData.error || 'Failed to send email');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Sending failed');
    } finally {
      setIsSending(false);
      setTimeout(() => setToastMessage(null), 6000);
    }
  };

  const filteredMessages = messages.filter((m) => {
    const matchesFilter = filterType === 'ALL' || m.status === filterType;
    const matchesSearch =
      m.subject.toLowerCase().includes(search.toLowerCase()) ||
      m.snippet.toLowerCase().includes(search.toLowerCase()) ||
      (m.extractedTicketNumber && m.extractedTicketNumber.toLowerCase().includes(search.toLowerCase())) ||
      (m.extractedBranchCode && m.extractedBranchCode.toLowerCase().includes(search.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  const isConnected = Boolean(
    verifiedGmailUser && verifiedGmailUser.emailAddress?.toLowerCase() === 'naeembuilder48@gmail.com'
  );

  return (
    <div className="space-y-6">
      {/* Real OAuth Live Connection Status Banner */}
      <div className={`p-4 border rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs ${
        isConnected
          ? 'bg-emerald-50/90 border-emerald-300 text-emerald-950'
          : 'bg-amber-50/90 border-amber-300 text-amber-950'
      }`}>
        <div className="flex items-start sm:items-center gap-3">
          <div className={`p-2 rounded-lg ${isConnected ? 'bg-emerald-200 text-emerald-800' : 'bg-amber-200 text-amber-800'}`}>
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider ${
                isConnected
                  ? 'bg-emerald-700 text-white'
                  : 'bg-amber-600 text-white'
              }`}>
                {isConnected ? 'CONNECTED — READ-ONLY' : 'DISCONNECTED / SIMULATION MODE'}
              </span>
              <span className="text-xs font-semibold">
                Account: {verifiedGmailUser?.emailAddress || 'naeembuilder48@gmail.com'}
              </span>
            </div>
            <p className="text-xs mt-0.5 opacity-90">
              {isConnected
                ? `Authorized via Google Identity Services OAuth 2.0 (Scope: gmail.readonly). Live mailbox reading enabled. Email sending is strictly disabled (Read-Only mode).`
                : 'Google OAuth token is missing or not yet authorized. Click "Connect Google Gmail" below to authorize read-only access for naeembuilder48@gmail.com.'}
            </p>
            <div className="text-[10px] opacity-75 mt-1 flex flex-wrap gap-x-3 gap-y-0.5 font-mono">
              <span>Client ID: {activeClientId ? `${activeClientId.slice(0, 16)}...` : 'Not configured'}</span>
              <span>Origin: {typeof window !== 'undefined' ? window.location.origin : 'N/A'}</span>
              <span>Scope: gmail.readonly</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
          {!isConnected ? (
            <button
              onClick={handleConnectGoogle}
              disabled={isConnecting}
              className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-bold rounded-lg bg-red-600 hover:bg-red-700 text-white shadow-sm transition-all disabled:opacity-50"
            >
              <KeyRound className="w-3.5 h-3.5" />
              {isConnecting ? 'Connecting...' : 'Connect Google Gmail'}
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleConnectGoogle}
                disabled={isConnecting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-100 shadow-xs transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isConnecting ? 'animate-spin' : ''}`} />
                Reconnect
              </button>
              <button
                onClick={handleDisconnect}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 shadow-xs transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" />
                Disconnect
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Top Banner & Title */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-2 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Gmail Operations Center
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-red-100 text-red-800 border border-red-200 flex items-center gap-1">
              <Mail className="w-3 h-3 text-red-600" />
              naeembuilder48@gmail.com
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Automated mailbox monitoring for HERE4U dispatches, regex ticket & branch code extraction, and read-only intake parser.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={() => handleSyncNowWithToken()}
            disabled={isSyncing}
            className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-blue-600 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Syncing...' : 'Sync Mailbox'}
          </button>

          <button
            disabled={true}
            title="Email sending disabled: User authorized Gmail in Read-Only mode (gmail.readonly). Sending emails is restricted."
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg bg-slate-200 text-slate-500 cursor-not-allowed shadow-none"
          >
            <Send className="w-3.5 h-3.5 text-slate-400" />
            Send Email (Read-Only)
          </button>
        </div>
      </div>

      {errorMessage && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs font-medium text-red-900 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
            <span>{errorMessage}</span>
          </div>
          <button
            onClick={() => setErrorMessage(null)}
            className="text-red-700 hover:text-red-900 text-xs font-bold px-2 py-0.5 rounded"
          >
            Dismiss
          </button>
        </div>
      )}

      {toastMessage && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-medium text-emerald-900 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Gmail Inbox Split Pane: Messages List + Detailed Reader */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[580px]">
        {/* Left Column: Email Thread List */}
        <div className="lg:col-span-5 bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col shadow-xs">
          {/* Search & Filter Toolbar */}
          <div className="p-3 border-b border-slate-200 space-y-2.5 bg-slate-50/60">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search subject, ticket, branch..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center gap-1 overflow-x-auto text-[11px]">
              {(['ALL', 'TICKET_MATCHED', 'REQUIRES_MANUAL_REVIEW', 'TEMP_CASE_CREATED', 'PARSED'] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setFilterType(mode)}
                  className={`px-2.5 py-1 rounded-md font-medium transition-colors whitespace-nowrap ${
                    filterType === mode
                      ? 'bg-blue-700 text-white'
                      : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  {mode.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Email Item Rows */}
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {filteredMessages.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-400">
                No email threads match the current filter.
              </div>
            ) : (
              filteredMessages.map((msg) => {
                const isSelected = selectedMessage?.id === msg.id;
                return (
                  <div
                    key={msg.id}
                    onClick={() => setSelectedMessage(msg)}
                    className={`p-3.5 cursor-pointer transition-colors text-left ${
                      isSelected ? 'bg-blue-50/70 border-l-4 border-l-blue-600' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                      <span className="font-semibold text-slate-800 truncate max-w-[200px]">
                        {msg.fromName || msg.fromEmail}
                      </span>
                      <span>{new Date(msg.receivedAt).toLocaleDateString()}</span>
                    </div>

                    <div className="text-xs font-bold text-slate-900 truncate mb-1">
                      {msg.subject}
                    </div>

                    <p className="text-[11px] text-slate-500 line-clamp-2 leading-relaxed mb-2">
                      {msg.snippet || msg.body}
                    </p>

                    {/* Metadata Extraction Pills */}
                    <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
                      {msg.isSimulated === false ? (
                        <span className="px-1.5 py-0.5 rounded bg-emerald-600 text-white font-bold tracking-wider">
                          LIVE GMAIL
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 font-medium">
                          SIMULATED
                        </span>
                      )}
                      {msg.extractedTicketNumber && (
                        <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 font-mono font-bold">
                          TKT: {msg.extractedTicketNumber}
                        </span>
                      )}
                      {msg.extractedBranchCode && (
                        <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-mono">
                          {msg.extractedBranchCode}
                        </span>
                      )}
                      <span
                        className={`px-1.5 py-0.5 rounded font-medium ${
                          msg.status === 'TICKET_MATCHED'
                            ? 'bg-emerald-100 text-emerald-800'
                            : msg.status === 'TEMP_CASE_CREATED'
                            ? 'bg-amber-100 text-amber-800'
                            : msg.status === 'REQUIRES_MANUAL_REVIEW'
                            ? 'bg-rose-100 text-rose-800'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {msg.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Column: Email Reader & Extracted Actions */}
        <div className="lg:col-span-7 bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col shadow-xs">
          {selectedMessage ? (
            <div className="p-6 flex flex-col h-full space-y-5">
              {/* Header */}
              <div className="border-b border-slate-200 pb-4 space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <h2 className="text-base sm:text-lg font-bold text-slate-900 leading-snug">
                    {selectedMessage.subject}
                  </h2>
                  <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-100 text-slate-700 shrink-0">
                    Thread ID: {selectedMessage.threadId.substring(0, 10)}...
                  </span>
                </div>

                <div className="flex flex-wrap items-center justify-between text-xs text-slate-500 gap-2">
                  <div>
                    <span className="font-semibold text-slate-700">From: </span>
                    {selectedMessage.fromName} &lt;{selectedMessage.fromEmail}&gt;
                  </div>
                  <div>
                    {new Date(selectedMessage.receivedAt).toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Extraction Diagnostic Bar */}
              <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-lg space-y-2 text-xs">
                <div className="font-bold text-slate-700 text-[11px] uppercase tracking-wider">
                  HERE4U Automated Parser Extraction
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <span className="text-[11px] text-slate-500 block">Extracted Ticket Number</span>
                    <span className="font-mono font-bold text-slate-900 text-xs">
                      {selectedMessage.extractedTicketNumber || (
                        <span className="text-amber-600 font-normal italic">None (Awaiting TKT)</span>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-[11px] text-slate-500 block">Extracted Branch Code</span>
                    <span className="font-mono font-bold text-slate-900 text-xs">
                      {selectedMessage.extractedBranchCode || 'Not specified'}
                    </span>
                  </div>
                  <div>
                    <span className="text-[11px] text-slate-500 block">Identified Work Type</span>
                    <span className="font-medium text-slate-900 text-xs">
                      {selectedMessage.extractedWorkType || 'General Civil Works'}
                    </span>
                  </div>
                </div>

                <div className="pt-2 flex flex-wrap items-center justify-between gap-2 border-t border-slate-200">
                  <span className="text-[11px] text-slate-500">
                    Workflow status:{' '}
                    <span
                      className={`font-semibold px-2 py-0.5 rounded text-[11px] ${
                        selectedMessage.status === 'TICKET_MATCHED'
                          ? 'bg-emerald-100 text-emerald-800'
                          : selectedMessage.status === 'TEMP_CASE_CREATED'
                          ? 'bg-amber-100 text-amber-800'
                          : selectedMessage.status === 'REQUIRES_MANUAL_REVIEW'
                          ? 'bg-rose-100 text-rose-800'
                          : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {selectedMessage.status.replace(/_/g, ' ')}
                    </span>
                  </span>
                  {selectedMessage.status === 'TEMP_CASE_CREATED' && (
                    <button
                      onClick={() => onNavigate('temp-cases')}
                      className="text-xs font-bold text-blue-700 hover:underline flex items-center gap-1"
                    >
                      Open Temporary Cases &rarr;
                    </button>
                  )}
                  {selectedMessage.status === 'TICKET_MATCHED' && (
                    <button
                      onClick={() => onNavigate('tickets', selectedMessage.matchedTicketId ? String(selectedMessage.matchedTicketId) : undefined)}
                      className="text-xs font-bold text-emerald-700 hover:underline flex items-center gap-1"
                    >
                      View Linked Ticket &rarr;
                    </button>
                  )}
                </div>
              </div>

              {/* Manual Review Callout & Action Bar (Requirement 7) */}
              {selectedMessage.status === 'REQUIRES_MANUAL_REVIEW' && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-3">
                  <div className="flex items-start gap-2.5">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <h4 className="text-xs font-bold text-amber-900">
                        Supervisor Manual Review Required
                      </h4>
                      <p className="text-[11px] text-amber-700 mt-0.5">
                        {selectedMessage.reviewReason ||
                          `Inbound message references complaint number '${selectedMessage.extractedTicketNumber || 'Unknown'}' which does not match an open ticket in the ERP.`}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2.5 pt-2 border-t border-amber-200/70">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedReviewTicketId('');
                        setSelectedReviewTempCaseId('');
                        setManualTicketNumberInput(selectedMessage.extractedTicketNumber || '');
                        setShowManualReviewModal('LINK_TICKET');
                      }}
                      className="px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white shadow-xs inline-flex items-center gap-1.5 transition-colors"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Link to Existing Ticket
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedReviewTicketId('');
                        setSelectedReviewTempCaseId('');
                        setManualTempCaseNotes(
                          `Created via Manual Review in Gmail Center. Extracted reference: ${selectedMessage.extractedTicketNumber || 'None'}`
                        );
                        setShowManualReviewModal('CREATE_TEMP_CASE');
                      }}
                      className="px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-blue-700 hover:bg-blue-800 text-white shadow-xs inline-flex items-center gap-1.5 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Create Temporary Case
                    </button>
                    {temporaryCases.some((c) => c.status === 'PENDING_TICKET_NUMBER') && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedReviewTicketId('');
                          setSelectedReviewTempCaseId('');
                          setShowManualReviewModal('LINK_TEMP_CASE');
                        }}
                        className="px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-slate-700 hover:bg-slate-800 text-white shadow-xs inline-flex items-center gap-1.5 transition-colors"
                      >
                        <Link2 className="w-3.5 h-3.5" />
                        Link to Existing Temp Case
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Body Content */}
              <div className="flex-1 bg-slate-50/40 p-4 rounded-lg border border-slate-100 overflow-y-auto">
                <pre className="text-xs text-slate-800 font-sans whitespace-pre-wrap leading-relaxed">
                  {selectedMessage.body}
                </pre>
              </div>

              {/* Attachments if any */}
              {selectedMessage.hasAttachments && selectedMessage.attachments && (
                <div className="space-y-1.5">
                  <div className="text-[11px] font-bold text-slate-600 uppercase flex items-center gap-1.5">
                    <Paperclip className="w-3.5 h-3.5 text-slate-400" />
                    Attached Images &amp; Evidence ({selectedMessage.attachments.length})
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedMessage.attachments.map((url, i) => (
                      <a
                        key={i}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded border border-slate-200"
                      >
                        <Eye className="w-3 h-3 text-slate-500" />
                        Attachment #{i + 1}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Toolbar */}
              <div className="pt-2 border-t border-slate-200 flex flex-wrap items-center justify-between gap-2">
                <div className="text-[11px] text-slate-500">
                  Logged in Naeem Builder ERP
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setComposeForm((prev) => ({
                        ...prev,
                        to: selectedMessage.fromEmail,
                        subject: `RE: ${selectedMessage.subject}`,
                      }));
                      setShowComposer(true);
                    }}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-700 text-white hover:bg-blue-800 transition-colors inline-flex items-center gap-1.5"
                  >
                    <Send className="w-3.5 h-3.5" />
                    Reply
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="m-auto p-12 text-center text-slate-400">
              <Mail className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-xs">Select an email message to view details and parser extractions.</p>
            </div>
          )}
        </div>
      </div>

      {/* Compose / Send Email Modal */}
      {showComposer && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                <Send className="w-4 h-4 text-blue-700" />
                <h3 className="text-sm font-bold text-slate-900">Compose Email via Gmail API</h3>
              </div>
              <button
                onClick={() => setShowComposer(false)}
                className="text-slate-400 hover:text-slate-600 text-lg leading-none"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSendEmail} className="p-5 space-y-3.5 text-xs">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">To Recipient</label>
                <input
                  type="email"
                  required
                  value={composeForm.to}
                  onChange={(e) => setComposeForm({ ...composeForm, to: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Subject</label>
                <input
                  type="text"
                  required
                  value={composeForm.subject}
                  onChange={(e) => setComposeForm({ ...composeForm, subject: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Linked Ticket (Optional)</label>
                <select
                  value={composeForm.ticketId}
                  onChange={(e) => setComposeForm({ ...composeForm, ticketId: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="">None / General Inquiry</option>
                  {tickets.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.ticketNumber} — {t.branchName} ({t.subject})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Message Body</label>
                <textarea
                  rows={6}
                  required
                  value={composeForm.body}
                  onChange={(e) => setComposeForm({ ...composeForm, body: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none font-sans"
                />
              </div>

              <div className="pt-2 border-t border-slate-200 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setShowComposer(false)}
                  className="px-3.5 py-2 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSending}
                  className="px-4 py-2 text-xs font-semibold rounded-lg bg-blue-700 hover:bg-blue-800 text-white transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  <Send className="w-3.5 h-3.5" />
                  {isSending ? 'Sending via Gmail...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Manual Review Action Modal (Requirement 7) */}
      {showManualReviewModal && selectedMessage && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                {showManualReviewModal === 'LINK_TICKET' ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                ) : showManualReviewModal === 'LINK_TEMP_CASE' ? (
                  <Link2 className="w-4 h-4 text-slate-700" />
                ) : (
                  <Plus className="w-4 h-4 text-blue-700" />
                )}
                <h3 className="text-sm font-bold text-slate-900">
                  {showManualReviewModal === 'LINK_TICKET'
                    ? 'Link Inbound Email to ERP Ticket'
                    : showManualReviewModal === 'LINK_TEMP_CASE'
                    ? 'Link Inbound Email to Existing Temporary Case'
                    : 'Create Temporary Case for Dispatch'}
                </h3>
              </div>
              <button
                onClick={() => setShowManualReviewModal(null)}
                className="text-slate-400 hover:text-slate-600 text-lg leading-none"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleManualReviewSubmit} className="p-5 space-y-4 text-xs">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1">
                <div className="font-semibold text-slate-800 line-clamp-1">{selectedMessage.subject}</div>
                <div className="text-[11px] text-slate-500">From: {selectedMessage.fromEmail}</div>
                {selectedMessage.extractedTicketNumber && (
                  <div className="text-[11px] text-blue-700 font-mono font-bold">
                    Detected Reference: {selectedMessage.extractedTicketNumber}
                  </div>
                )}
              </div>

              {showManualReviewModal === 'LINK_TICKET' ? (
                <div className="space-y-3.5">
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">
                      Select Existing ERP Ticket
                    </label>
                    <select
                      value={selectedReviewTicketId}
                      onChange={(e) => {
                        setSelectedReviewTicketId(e.target.value);
                        if (e.target.value) setManualTicketNumberInput('');
                      }}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="">-- Choose open ticket --</option>
                      {tickets.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.ticketNumber} — {t.branchName} ({t.status})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="relative flex py-1 items-center">
                    <div className="flex-grow border-t border-slate-200"></div>
                    <span className="shrink mx-2 text-[10px] text-slate-400 uppercase font-semibold">
                      Or Enter Ticket Number
                    </span>
                    <div className="flex-grow border-t border-slate-200"></div>
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">
                      Official Ticket Number (e.g. 130343, 130483, TKT-2026-001)
                    </label>
                    <input
                      type="text"
                      placeholder="Enter ticket or complaint number"
                      value={manualTicketNumberInput}
                      onChange={(e) => {
                        setManualTicketNumberInput(e.target.value);
                        if (e.target.value) setSelectedReviewTicketId('');
                      }}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono font-bold focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
              ) : showManualReviewModal === 'LINK_TEMP_CASE' ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">
                      Select Open Temporary Case
                    </label>
                    <select
                      value={selectedReviewTempCaseId}
                      onChange={(e) => setSelectedReviewTempCaseId(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="">-- Choose temporary case --</option>
                      {temporaryCases
                        .filter((c) => c.status === 'PENDING_TICKET_NUMBER')
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.tempCaseNumber} — {c.branchName} ({c.issueSubject})
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-[11px] text-slate-500 block">Branch Code</span>
                      <span className="font-mono font-bold text-slate-800 text-xs">
                        {selectedMessage.extractedBranchCode || 'UBL-0142'}
                      </span>
                    </div>
                    <div>
                      <span className="text-[11px] text-slate-500 block">Work Category</span>
                      <span className="font-medium text-slate-800 text-xs">
                        {selectedMessage.extractedWorkType || 'Paint / Tile / Seepage'}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">
                      Internal Dispatch Notes
                    </label>
                    <textarea
                      rows={3}
                      value={manualTempCaseNotes}
                      onChange={(e) => setManualTempCaseNotes(e.target.value)}
                      placeholder="Add instructions for field supervisor..."
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-slate-200 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setShowManualReviewModal(null)}
                  className="px-3.5 py-2 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingReview}
                  className={`px-4 py-2 text-xs font-semibold rounded-lg text-white transition-colors disabled:opacity-50 inline-flex items-center gap-1.5 ${
                    showManualReviewModal === 'LINK_TICKET'
                      ? 'bg-emerald-700 hover:bg-emerald-800'
                      : showManualReviewModal === 'LINK_TEMP_CASE'
                      ? 'bg-slate-800 hover:bg-slate-900'
                      : 'bg-blue-700 hover:bg-blue-800'
                  }`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {isSubmittingReview
                    ? 'Processing...'
                    : showManualReviewModal === 'LINK_TICKET'
                    ? 'Confirm & Link Ticket'
                    : showManualReviewModal === 'LINK_TEMP_CASE'
                    ? 'Confirm & Link Temp Case'
                    : 'Confirm & Create Temp Case'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
